import { gql, useMutation } from '@apollo/client';
import { StatusModel, TerminationReasonModel, TerminationReasonPatchModelInput } from '@utmost.co/enterprise-models';
import { useSnackbar } from 'src/utils/hooks/useSnackbar';
import { useTerminationReasonsIntl } from '../translations/useTerminationReasonsIntl';

const PATCH_TERMINATION_REASON = gql`
  mutation PatchTerminationReason($uid: String!, $model: TerminationReasonPatchModelInput) {
    patchTerminationReason(uid: $uid, model: $model) {
      assignable
      eligibleForRehire
      name
      referenceId
      status
      sourceName
      uid
    }
  }
`;

const ACTIVATE_TERMINATION_REASON = gql`
  mutation ActivateTerminationReason($uid: String!) {
    activateTerminationReason(uid: $uid) {
      uid
    }
  }
`;

const DEACTIVATE_TERMINATION_REASON = gql`
  mutation DeactivateTerminationReason($uid: String!) {
    deactivateTerminationReason(uid: $uid) {
      uid
    }
  }
`;

export function usePatchTerminationReasons() {
  const { enqueueSnackbar, showError } = useSnackbar();
  const intl = useTerminationReasonsIntl();
  const [updateTerminationReason, { loading: updateLoading }] = useMutation<{
    patchTerminationReason: TerminationReasonModel;
  }>(PATCH_TERMINATION_REASON);

  const [activateTerminationReason, { loading: activeLoading }] = useMutation<
    {
      patchTerminationReason: TerminationReasonModel;
    },
    { uid: string; model?: DeepPartial<TerminationReasonPatchModelInput> }
  >(ACTIVATE_TERMINATION_REASON);

  const [deactivateTerminationReason, { loading: deactivateLoading }] = useMutation<
    {
      patchTerminationReason: TerminationReasonModel;
    },
    { uid: string }
  >(DEACTIVATE_TERMINATION_REASON);

  const patchTerminationReason = async (
    uid: string,
    status: StatusModel,
    model: Partial<TerminationReasonPatchModelInput>
  ) => {
    try {
      if (status === 'ACTIVE') await activateTerminationReason({ variables: { uid } });
      else await deactivateTerminationReason({ variables: { uid } });

      const { data } = await updateTerminationReason({ variables: { uid, model } });

      enqueueSnackbar(intl.updateSuccess, {
        variant: 'success'
      });

      return data?.patchTerminationReason;
    } catch (e: any) {
      let message = e.toString();
      if (Array.isArray(e) && e?.[0]?.message) {
        message = e?.[0]?.message;
      }

      showError(message);
      return null;
    }
  };

  return {
    patchTerminationReason,
    loading: updateLoading || activeLoading || deactivateLoading || false
  };
}
